import React, { useEffect, useState } from 'react';
import SimpleAppointmentService from '../appointments/SimpleAppointmentService';
import DoctorInfoService from '../doctorInfo/doctorInfoService';
import OfficeService from '../office/officeService';
import patientInfoService from '../patientInfo/patientInfoService';
import './FrontDeskCheckIn.css';

const DEFAULT_NOT_SURE_DOCTOR_ID = 15;

const FrontDeskCheckIn = () => {
  const [appointments, setAppointments] = useState([]);
  const [offices, setOffices] = useState([]);
  const [selectedOfficeId, setSelectedOfficeId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  const [successMessage, setSuccessMessage] = useState('');
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [saving, setSaving] = useState(false);

  const [patientNames, setPatientNames] = useState({});
  const [doctorNames, setDoctorNames] = useState({});

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    patientId: '',
    officeId: '',
    doctorId: DEFAULT_NOT_SURE_DOCTOR_ID,
    appointmentDate: '',
    appointmentTime: '',
    notes: '',
    conformation: 'Scheduled',
  });

  const [allTimeSlots, setAllTimeSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingTimes, setLoadingTimes] = useState(false);

  const appointmentService = new SimpleAppointmentService();
  const doctorService = new DoctorInfoService();
  const officeService = new OfficeService();
  const patientService = new patientInfoService();

  const getRole = () => {
    return localStorage.getItem('role')?.replace('ROLE_', '') || '';
  };

  const role = getRole();
  const isAdmin = role === 'ADMIN';

  const getAppointmentId = (appt) => {
    return appt.simpleAppointmentId || appt.simple_appointment_id;
  };

  const isCompleted = (appt) => {
    return appt?.conformation?.toLowerCase() === 'completed';
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const formatTime12Hour = (timeValue) => {
    if (!timeValue) return '';

    const parts = String(timeValue).split(':');
    let hour = parseInt(parts[0], 10);
    const minute = parts[1] || '00';

    if (Number.isNaN(hour)) {
      return timeValue;
    }

    const suffix = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;

    if (hour === 0) {
      hour = 12;
    }

    return `${hour}:${minute} ${suffix}`;
  };

  const sortAppointmentsByTime = (list) => {
    return [...list].sort((a, b) =>
      String(a.appointmentTime || '').localeCompare(String(b.appointmentTime || ''))
    );
  };

  useEffect(() => {
    const fetchOfficesAndInitialAppointments = async () => {
      try {
        let officeList = [];

        if (['ADMIN', 'MANAGER', 'FRONTDESK'].includes(role)) {
          officeList = await officeService.getAllOffices();
        } else {
          const doctorId = sessionStorage.getItem('doctorId');

          if (doctorId) {
            const doctor = await doctorService.getDoctorById(doctorId);
            const office = await officeService.getOfficeById(doctor.officeId);
            officeList = [office];
          } else {
            officeList = await officeService.getAllOffices();
          }
        }

        setOffices(officeList);

        const defaultOfficeId = officeList[0]?.officeId;

        if (defaultOfficeId) {
          setSelectedOfficeId(defaultOfficeId);
          await fetchAppointments(defaultOfficeId, selectedDate);
        }
      } catch (error) {
        console.error('Error fetching offices or appointments:', error);
      }
    };

    fetchOfficesAndInitialAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedOfficeId && selectedDate) {
      fetchAppointments(selectedOfficeId, selectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOfficeId, selectedDate]);

  const fetchAppointments = async (officeId, date) => {
    try {
      setLoadingAppointments(true);

      const simpleAppointments =
        await appointmentService.getSimpleAppointmentsByOfficeAndDate(
          officeId,
          date
        );

      const list = sortAppointmentsByTime(
        Array.isArray(simpleAppointments) ? simpleAppointments : []
      );

      setAppointments(list);

      const newPatientNames = {};
      const newDoctorNames = {};

      for (const appt of list) {
        const appointmentId = getAppointmentId(appt);

        if (appt.patientId) {
          try {
            const patient = await patientService.getPatientById(appt.patientId);
            newPatientNames[appointmentId] = `${patient.firstName} ${patient.lastName}`;
          } catch (err) {
            console.error(
              'Error loading patient for appointment:',
              appointmentId,
              err
            );
            newPatientNames[appointmentId] = 'Unknown patient';
          }
        } else {
          newPatientNames[appointmentId] = 'No patient';
        }

        if (appt.doctorId) {
          try {
            const doctor = await doctorService.getDoctorById(appt.doctorId);

            newDoctorNames[appointmentId] =
              `${doctor.firstname || doctor.firstName || ''} ${
                doctor.lastname || doctor.lastName || ''
              }`.trim() || `Doctor #${appt.doctorId}`;
          } catch {
            newDoctorNames[appointmentId] = 'Not Sure';
          }
        } else {
          newDoctorNames[appointmentId] = 'Not Sure';
        }
      }

      setPatientNames(newPatientNames);
      setDoctorNames(newDoctorNames);
    } catch (error) {
      console.error('Error loading simple appointments:', error);
      setAppointments([]);
    } finally {
      setLoadingAppointments(false);
    }
  };

  const fetchAvailableTimes = async (officeId, date) => {
    if (!officeId || !date) {
      setAllTimeSlots([]);
      setBookedSlots([]);
      return;
    }

    try {
      setLoadingTimes(true);

      const response = await appointmentService.getAvailableTimes(
        officeId,
        date
      );

      setAllTimeSlots(response?.allSlots || []);
      setBookedSlots(response?.bookedSlots || []);
    } catch (err) {
      console.error('Failed to load time slots:', err);
      setAllTimeSlots([]);
      setBookedSlots([]);
      alert(err.message || 'Could not load available times.');
    } finally {
      setLoadingTimes(false);
    }
  };

  const handleStatusUpdate = async (appt, status) => {
    const id = getAppointmentId(appt);

    if (isCompleted(appt) && !isAdmin) {
      alert('Completed appointments are locked.');
      return;
    }

    try {
      setSaving(true);

      await appointmentService.updateConformationOnly(id, status);

      setAppointments((prev) =>
        prev.map((item) =>
          getAppointmentId(item) === id
            ? { ...item, conformation: status }
            : item
        )
      );

      showSuccess(`Status updated to "${status}" successfully!`);
    } catch (error) {
      console.error('Error updating simple appointment status:', error);
      alert(error.message || 'Error updating appointment status.');
    } finally {
      setSaving(false);
    }
  };

  const confirmAction = async (message, actionCallback) => {
    const confirmed = window.confirm(message);

    if (confirmed) {
      await actionCallback();
    }
  };

  const toggleExpand = (id, field) => {
    setAppointments((prev) =>
      prev.map((appt) =>
        getAppointmentId(appt) === id
          ? { ...appt, [`${field}Expanded`]: !appt[`${field}Expanded`] }
          : appt
      )
    );
  };

  const startEdit = async (appt) => {
    if (isCompleted(appt) && !isAdmin) {
      alert('Completed appointments are locked.');
      return;
    }

    const id = getAppointmentId(appt);

    const nextForm = {
      patientId: appt.patientId || '',
      officeId: appt.officeId || selectedOfficeId || '',
      doctorId: appt.doctorId || DEFAULT_NOT_SURE_DOCTOR_ID,
      appointmentDate: appt.appointmentDate || selectedDate,
      appointmentTime: appt.appointmentTime || '',
      notes: appt.notes || '',
      conformation: appt.conformation || 'Scheduled',
    };

    setEditingId(id);
    setEditForm(nextForm);

    await fetchAvailableTimes(nextForm.officeId, nextForm.appointmentDate);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({
      patientId: '',
      officeId: '',
      doctorId: DEFAULT_NOT_SURE_DOCTOR_ID,
      appointmentDate: '',
      appointmentTime: '',
      notes: '',
      conformation: 'Scheduled',
    });
    setAllTimeSlots([]);
    setBookedSlots([]);
  };

  const handleEditChange = async (key, value) => {
    setEditForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === 'appointmentDate' ? { appointmentTime: '' } : {}),
    }));

    if (key === 'appointmentDate') {
      await fetchAvailableTimes(editForm.officeId, value);
    }
  };

  const handleTimeSelect = (time) => {
    setEditForm((prev) => ({
      ...prev,
      appointmentTime: time,
    }));
  };

  const saveEdit = async (appt) => {
    const id = getAppointmentId(appt);

    if (!editForm.patientId) {
      alert('Patient is required.');
      return;
    }

    if (!editForm.officeId) {
      alert('Office is required.');
      return;
    }

    if (!editForm.appointmentDate) {
      alert('Date is required.');
      return;
    }

    if (!editForm.appointmentTime) {
      alert('Time is required.');
      return;
    }

    const finalData = {
      ...appt,
      patientId: parseInt(editForm.patientId, 10),
      officeId: parseInt(editForm.officeId, 10),
      doctorId: parseInt(
        editForm.doctorId || DEFAULT_NOT_SURE_DOCTOR_ID,
        10
      ),
      appointmentDate: editForm.appointmentDate,
      appointmentTime: editForm.appointmentTime,
      notes: editForm.notes,
      conformation: editForm.conformation || appt.conformation || 'Scheduled',
    };

    try {
      setSaving(true);

      const result = await appointmentService.updateSimpleAppointment(
        id,
        finalData
      );

      if (result?.success === false) {
        throw new Error(result?.message || 'Could not update appointment.');
      }

      await fetchAppointments(selectedOfficeId, selectedDate);
      cancelEdit();
      showSuccess('Appointment updated successfully.');
    } catch (err) {
      console.error('Failed to update appointment:', err);
      alert(err.message || 'Could not update appointment.');
    } finally {
      setSaving(false);
    }
  };

  const deleteAppointment = async (appt) => {
    const id = getAppointmentId(appt);

    if (isCompleted(appt) && !isAdmin) {
      alert('Completed appointments can only be deleted by ADMIN.');
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to delete this appointment?'
    );

    if (!confirmed) return;

    try {
      setSaving(true);

      const result = await appointmentService.deleteSimpleAppointment(id);

      if (result?.success === false) {
        throw new Error(result?.message || 'Could not delete appointment.');
      }

      await fetchAppointments(selectedOfficeId, selectedDate);
      showSuccess('Appointment deleted successfully.');
    } catch (err) {
      console.error('Failed to delete appointment:', err);
      alert(err.message || 'Could not delete appointment.');
    } finally {
      setSaving(false);
    }
  };

  const renderStatusButtons = (appt) => {
    if (appt.conformation === 'Scheduled') {
      return (
        <>
          <button
            disabled={saving}
            onClick={() => handleStatusUpdate(appt, 'Walked In')}
          >
            Walk In
          </button>

          <button
            disabled={saving}
            onClick={() =>
              confirmAction(
                'Are you sure you want to cancel this appointment?',
                () => handleStatusUpdate(appt, 'Canceled')
              )
            }
          >
            Cancel
          </button>

          <button
            disabled={saving}
            onClick={() =>
              confirmAction(
                'Are you sure you want to mark this appointment as No Show?',
                () => handleStatusUpdate(appt, 'No Show')
              )
            }
          >
            No Show
          </button>
        </>
      );
    }

    if (appt.conformation === 'Walked In') {
      return (
        <button
          disabled={saving}
          onClick={() => handleStatusUpdate(appt, 'In Room')}
        >
          In Room
        </button>
      );
    }

    if (appt.conformation === 'In Room') {
      return (
        <button
          disabled={saving}
          onClick={() => handleStatusUpdate(appt, 'Completed')}
        >
          Completed
        </button>
      );
    }

    return (
      <div className="canceled-message">
        {appt.conformation || 'Scheduled'}
      </div>
    );
  };

  return (
    <div className="frontdesk-container frontdesk-wide">
      {loadingAppointments && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="spinner"></div>
            <div>Loading appointments...</div>
          </div>
        </div>
      )}

      <h1>Front Desk Check-In</h1>

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      <div className="controls compact-controls">
        <label>
          Select Office:
          <select
            value={selectedOfficeId || ''}
            onChange={(e) => {
              setSelectedOfficeId(e.target.value);
              cancelEdit();
            }}
          >
            {offices.map((office) => (
              <option key={office.officeId} value={office.officeId}>
                {office.officeName}
              </option>
            ))}
          </select>
        </label>

        <label>
          Select Date:
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              cancelEdit();
            }}
          />
        </label>
      </div>

      <div className="appointments-grid">
        {!loadingAppointments && appointments.length === 0 && (
          <div className="appointment-card compact-appointment-card">
            <div className="appointment-main">
              No appointments found for this office/date.
            </div>
          </div>
        )}

        {appointments.map((appt) => {
          const appointmentId = getAppointmentId(appt);
          const completed = isCompleted(appt);
          const isEditing = editingId === appointmentId;
          const canEdit = !completed || isAdmin;
          const canDelete = !completed || isAdmin;
          const isDetailsExpanded = !!appt.detailsExpanded;
          const isNotesExpanded = !!appt.notesExpanded;

          return (
            <div
              key={appointmentId}
              className={`appointment-card compact-appointment-card ${
                appt.conformation === 'Canceled' ||
                appt.conformation === 'No Show'
                  ? 'canceled'
                  : ''
              } ${completed ? 'completed-card' : ''}`}
            >
              {!isEditing ? (
                <>
                  <div className="compact-card-header">
                    <div className="compact-time">
                      {formatTime12Hour(appt.appointmentTime)}
                    </div>

                    <div className="compact-status">
                      {appt.conformation || 'Scheduled'}
                    </div>
                  </div>

                  <div className="compact-patient">
                    {patientNames[appointmentId] || 'Loading...'}
                  </div>

                  <div className="compact-doctor">
                    Doctor: {doctorNames[appointmentId] || 'Not Sure'}
                  </div>

                  {completed && (
                    <div className="canceled-message compact-lock">
                      Completed - locked
                    </div>
                  )}

                  <div className="compact-card-actions">
                    <button
                      type="button"
                      onClick={() => toggleExpand(appointmentId, 'details')}
                    >
                      {isDetailsExpanded ? 'Hide Actions' : 'Actions'}
                    </button>

                    <button
                      type="button"
                      onClick={() => toggleExpand(appointmentId, 'notes')}
                    >
                      {isNotesExpanded ? 'Hide Notes' : 'Notes'}
                    </button>
                  </div>

                  {isNotesExpanded && (
                    <div className="expand-content compact-expand">
                      <strong>Notes:</strong> {appt.notes || 'None'}
                    </div>
                  )}

                  {isDetailsExpanded && (
                    <div className="compact-expanded-panel">
                      <div className="status-buttons compact-status-buttons">
                        {renderStatusButtons(appt)}
                      </div>

                      <div className="compact-edit-delete">
                        {canEdit && (
                          <button
                            type="button"
                            className="edit-button"
                            disabled={saving}
                            onClick={() => startEdit(appt)}
                          >
                            Edit
                          </button>
                        )}

                        {canDelete && (
                          <button
                            type="button"
                            className="cancel-button"
                            disabled={saving}
                            onClick={() => deleteAppointment(appt)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="compact-card-header">
                    <div className="compact-time">Editing</div>
                    <div className="compact-status">#{appointmentId}</div>
                  </div>

                  <div className="compact-patient">
                    {patientNames[appointmentId] || 'Loading...'}
                  </div>

                  <label>Date</label>
                  <input
                    type="date"
                    value={editForm.appointmentDate}
                    onChange={(e) =>
                      handleEditChange('appointmentDate', e.target.value)
                    }
                  />

                  <label>Available Time Slots</label>

                  {loadingTimes ? (
                    <p>Loading available times...</p>
                  ) : allTimeSlots.length === 0 ? (
                    <p>No available office hours for this date.</p>
                  ) : (
                    <div className="time-slot-list compact-time-slots">
                      {allTimeSlots.map((time, index) => {
                        const isCurrentOriginalTime =
                          time === appt.appointmentTime &&
                          editForm.appointmentDate === appt.appointmentDate &&
                          parseInt(editForm.officeId, 10) ===
                            parseInt(appt.officeId, 10);

                        const isBooked =
                          bookedSlots.includes(time) && !isCurrentOriginalTime;

                        const isSelected = time === editForm.appointmentTime;

                        return (
                          <button
                            key={`${time}-${index}`}
                            type="button"
                            disabled={isBooked}
                            className={`${
                              isBooked ? 'booked-slot' : 'available-slot'
                            } ${isSelected ? 'selected-slot' : ''}`}
                            onClick={() => !isBooked && handleTimeSelect(time)}
                          >
                            {formatTime12Hour(time)}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <label>Notes</label>
                  <textarea
                    value={editForm.notes}
                    onChange={(e) =>
                      handleEditChange('notes', e.target.value)
                    }
                    placeholder="Appointment notes"
                  />

                  <label>Status</label>
                  <select
                    value={editForm.conformation}
                    onChange={(e) =>
                      handleEditChange('conformation', e.target.value)
                    }
                  >
                    <option value="Scheduled">Scheduled</option>
                    <option value="Walked In">Walked In</option>
                    <option value="In Room">In Room</option>
                    <option value="Completed">Completed</option>
                    <option value="Canceled">Canceled</option>
                    <option value="No Show">No Show</option>
                  </select>

                  <div className="compact-edit-delete">
                    <button
                      type="button"
                      className="save-button"
                      disabled={saving}
                      onClick={() => saveEdit(appt)}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>

                    <button
                      type="button"
                      className="cancel-button"
                      disabled={saving}
                      onClick={cancelEdit}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FrontDeskCheckIn;